import type { HTMLAttributes } from 'react';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon';
import styles from './Pill.module.css';

// Pill — DESIGN_SYSTEM §12 row 9 (`.pill`). Colour + icon + label, always: a tone without an icon is refused
// in development (the `neutral` tone, which has no colour, and the `dot` variant, whose dot is the second
// cue, are the exceptions). Health verdicts ("Going well"), rankings ("Best value") and the delivery
// statuses (StatusPill) all render through this one component.

export type PillTone =
  'good' | 'watch' | 'bad' | 'paused' | 'learning' | 'info' | 'neutral' | 'staged';

export interface PillProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  tone: PillTone;
  icon?: IconName;
  /** Dense tables: a coloured dot instead of the tinted pill (the icon hides, the label stays). */
  dot?: boolean;
  size?: 'sm' | 'md';
  children: string;
  testID?: string;
}

const TONE_CLASS: Record<PillTone, string | undefined> = {
  good: styles.good,
  watch: styles.watch,
  bad: styles.bad,
  paused: undefined,
  learning: styles.learning,
  info: styles.info,
  neutral: styles.neutral,
  staged: styles.staged,
};

export function Pill({
  tone,
  icon,
  dot = false,
  size = 'md',
  children,
  testID,
  className,
  ...rest
}: PillProps) {
  if (import.meta.env.DEV && !icon && !dot && tone !== 'neutral') {
    throw new Error(
      `Pill "${children}": a coloured status needs an icon (DESIGN_SYSTEM §3, never colour alone).`,
    );
  }
  return (
    <span
      className={cx(
        styles.pill,
        TONE_CLASS[tone],
        size === 'sm' && styles.sm,
        dot && styles.dot,
        className,
      )}
      data-tone={tone}
      data-testid={testID}
      {...rest}
    >
      {icon ? <Icon name={icon} size={14} /> : null}
      {children}
    </span>
  );
}
